import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, inject, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ApplicationInfoModel } from '../../models/application-info.model';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { catchError, concatMap, finalize, of, Subscription, tap, throwError } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { PageEvent } from '@angular/material/paginator';
import { FormService } from 'app/services/form.service';
import { IAppFormLink } from '../app-form-link';
import { Router } from '@angular/router';
import formLinksBuilder from '../form-links-builder';

import { ConfirmDeleteDialogComponent } from '../forms/common-sections/dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { ApplicationInfoService, RetrieveApplRequest } from '../landing-page.service';
import { AlertsService } from 'app/ui-components/alerts/alerts.service';
import { UserService } from 'app/users/user.service';
import { FormConstants } from 'app/constants/form.constants';
import { AlertMessagesConfig } from 'config/alert-messages.config';

@Component({
  selector: 'app-draft-applications-list',
  templateUrl: './draft-applications-list.component.html',
  styleUrls: ['./draft-applications-list.component.scss']
})
export class DraftApplicationsListComponent implements OnInit, OnDestroy {
  @Input() draftApplications!: ApplicationInfoModel[];
  private sizeChangeSubscription!: Subscription;
  private mobileDeviceSize: boolean = false;
  formLinks!: IAppFormLink[];

  currentPage = 0;
  paginatedDrafts: ApplicationInfoModel[] = [];    // Items to show on current page
  pageSize = 10;
  totalItems = 0;

  readonly dialog = inject(MatDialog);
  loadingComplete: boolean = true;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private applicationInfoService: ApplicationInfoService,
    private alertService: AlertsService,
    protected userService: UserService,
    private formService: FormService,
    public alertsMessageConfig: AlertMessagesConfig,
    private router: Router,
    private responsive: BreakpointObserver,
    private sortAnnouncer: LiveAnnouncer
  )
     {}

  ngOnInit(): void {
    this.formLinks = formLinksBuilder.build();
    
    this.sizeChangeSubscription = this.responsive.observe([Breakpoints.Handset]).subscribe(result => {
      if(result.matches) {
        this.mobileDeviceSize = true;
      } else {
        this.mobileDeviceSize = false;
      }
    });
  
  }
  
  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.updatePaginatedItems();
  }
 
   private updatePaginatedItems() {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedDrafts = this.draftApplications.slice(start, end);
    this.totalItems = this.draftApplications.length;
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['draftApplications'] && this.draftApplications) {
       this.totalItems = this.draftApplications.length;
      this.currentPage = 0; // reset to first page when new data comes inv
      this.updatePaginatedItems();
    }
  }
 
  ngOnDestroy(): void {
    this.sizeChangeSubscription.unsubscribe();
  }
  
  get applications() {
    return this.draftApplications;
  }

  get hasApplications() {
    if(!this.draftApplications) {
      return false;
    }

    return this.draftApplications.length;
  }

  get isMobileDeviceSize(): boolean {
    return this.mobileDeviceSize;
  }
  
  public openDialog(app: ApplicationInfoModel): void {
    this.alertService.removeAllAlerts();
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
        data:  app
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'no') {
        //DO nothing
      } else {
       this.deleteApp(app);
      }
    });
  }

  isAppInfoModelLocked(model: ApplicationInfoModel): boolean {
    return this.formService.isAppInfoModelLocked(model);
  }
  
  viewDraftApplication(appInfo: ApplicationInfoModel) {
    this.router.navigate(['/usan/application'],  
      { queryParams : { formType: this.formService.parseFormType(appInfo.formType), id: appInfo.caseSysId, source: 'applicant-dashboard-viewOnly'}});
  }

  openDraftApplication(appInfo: ApplicationInfoModel) {
    const formType = FormConstants.SERVICE_NOW_FORM_TYPE_MAP.get(appInfo.formType);
    if(!formType) {
      return;
    }
    
    let linkInfo = this.formLinkByFormName(formType);
    if(!linkInfo) {
      return;
    }

    this.formService.loadAndViewCase(appInfo, linkInfo);
  }
  
  formLinkByFormName(name: string): IAppFormLink | undefined {
    return this.formLinks.find(({ formName }) => formName === name);
  }

  deleteApp (app: ApplicationInfoModel) {
      var formIdReg = {caseSysId:app.formId} ;
      var caseSysIdReg = {caseSysId:app.caseSysId} 
      this.loadingComplete = false;

      of(formIdReg,caseSysIdReg).pipe(
        concatMap(param => 
          this.applicationInfoService.deleteApplication(param)
          .pipe(
            tap(() => {
              if (param === formIdReg) {
                //console.log('First delete call succeeded. Showing message success message...');
                this.alertService.displaySuccess("The application was successfully deleted.");
              }
            }),
            catchError(err => {
              if (param === formIdReg) {
                // Stop processing by re-throwing the error
                this.alertService.displayError("An error occurred while deleting the application.");
                return throwError(() => 'An error occurred while deleting the application.');
              } else {
                // Silently handle the error for the second call
                console.warn(`Error deleting application withcaseSysId  '${param}' call ignored.`, err);
                return of(null); // Continue even after error
              }
            })
          )
        ),
        finalize(() => {
          //console.log('All delete calls completed (success or error).');
          this.refreshDraftApplicationList();
        })
      ).subscribe(result => {
        //console.log('Delete Result:', result);
      });
     
  }
  
  private refreshDraftApplicationList () {
      const draftReq: RetrieveApplRequest = {
            orgId: this.userService.profile.orgId ?? '',
            ssoId: this.userService.profile.uniqueId ?? '',
            status: ["Draft"]
      }
      this.applicationInfoService.fetchApplicationsByStatus(draftReq).subscribe({
        next: (response) => {
          let data = response.Data;
          if (data) {
            this.draftApplications = this.applicationInfoService.mapToApplicationModelArray(data, 'Draft');
            this.updatePaginatedItems();
            this.loadingComplete = true;
          }
        },
        error: (err) => {
          console.log("Fetching Applications Error - " + err);
          this.loadingComplete = true;
        }
      });
  }

  sortChangeUpdate(sortChange: Sort) {
    const data = [... this.draftApplications];
    let column = sortChange.active;
    let dir = sortChange.direction;
    if (!column || dir === '') {
      //no change
      return;
    }
    const isAsc = dir ==='asc';
    this.draftApplications = data.sort((a,b) => {
      let aValue = typeof a[column] === 'string' ? a[column].toLowerCase() : a[column];
      let bValue = typeof b[column] === 'string' ? b[column].toLowerCase() : b[column];
      return this.compareValues(aValue, bValue, isAsc);
    
    });
    this.updatePaginatedItems();
    if(sortChange.direction) { //to announce sorting change for assistive technology
      this.sortAnnouncer.announce('Sorted '+ sortChange.direction);
    } else {
      this.sortAnnouncer.announce('Sort cleared');
    }
  }

  compareValues( a: string|number, b: string|number, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

}
